import {
  ISetupRepository,
  AdminClinicListItem,
  CreatePatientAppUserBody,
  CreatePatientAppUserResult,
  CreatePatientAppUserV2Body,
  CreatePatientAppUserV2Outcome,
  ReplacePatientAppUserV2Body,
  ReplacePatientAppUserV2Outcome,
  InviteFamilyMemberBody,
  InviteFamilyMemberOutcome,
  LinkedFamilyMember,
  AssignDeviceToPatientBody,
  PatientCentricApiError,
} from "../../adapters/driven/repositories/setupRepository";

export interface ISetupServiceError {
  message: string;
  status?: number;
}

export type ISetupService = ReturnType<typeof SetupServiceImplFactory>;

export const SetupServiceImplFactory = ({
  setupRepository,
}: {
  setupRepository: ISetupRepository;
}) => {
  const fetchAdminClinics = async (): IControllerPromiseResponse<
    AdminClinicListItem[],
    ISetupServiceError
  > => {
    try {
      const data = await setupRepository.fetchAdminClinicsAPI();
      return { success: true, data };
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        success: false,
        error: { message: e.message, status: e.status },
      };
    }
  };

  const createPatientAccount = async (
    input: CreatePatientAppUserBody
  ): IControllerPromiseResponse<
    CreatePatientAppUserResult,
    ISetupServiceError
  > => {
    try {
      const data = await setupRepository.createPatientAppUserAPI(input);
      return { success: true, data };
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        success: false,
        error: { message: e.message, status: e.status },
      };
    }
  };

  /**
   * v2 create. Repository owns HTTP-shape parsing and returns a
   * discriminated outcome — the service just funnels unexpected
   * throws into the `error` branch so the controller can rely on a
   * single result shape.
   */
  const createPatientAccountV2 = async (
    input: CreatePatientAppUserV2Body
  ): Promise<CreatePatientAppUserV2Outcome> => {
    try {
      return await setupRepository.createPatientAppUserV2API(input);
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        kind: "error",
        error:
          e instanceof PatientCentricApiError
            ? e
            : new PatientCentricApiError(
                e?.message ?? "Unexpected error",
                0,
                null
              ),
      };
    }
  };

  /**
   * Replace an existing patient. Same error-funnelling contract as
   * `createPatientAccountV2` above.
   */
  const replacePatientAccount = async (
    input: ReplacePatientAppUserV2Body
  ): Promise<ReplacePatientAppUserV2Outcome> => {
    try {
      return await setupRepository.replacePatientAppUserV2API(input);
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        kind: "error",
        error:
          e instanceof PatientCentricApiError
            ? e
            : new PatientCentricApiError(
                e?.message ?? "Unexpected error",
                0,
                null
              ),
      };
    }
  };

  /**
   * Invite a family member. Same error-funnelling contract as
   * `createPatientAccountV2` — the repository owns HTTP parsing and returns
   * a discriminated outcome; the service just catches unexpected throws.
   */
  const inviteFamilyMember = async (
    input: InviteFamilyMemberBody
  ): Promise<InviteFamilyMemberOutcome> => {
    try {
      return await setupRepository.inviteFamilyMemberAPI(input);
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        kind: "error",
        error:
          e instanceof PatientCentricApiError
            ? e
            : new PatientCentricApiError(
                e?.message ?? "Unexpected error",
                0,
                null
              ),
      };
    }
  };

  /**
   * Fetch the family members linked to a patient. Same result-shape
   * contract as `fetchAdminClinics` — repository throws, service funnels
   * into the success/error envelope.
   */
  const listFamilyMembers = async (
    patientId: number
  ): IControllerPromiseResponse<LinkedFamilyMember[], ISetupServiceError> => {
    try {
      const data = await setupRepository.listFamilyMembersAPI(patientId);
      return { success: true, data };
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        success: false,
        error: { message: e.message, status: e.status },
      };
    }
  };

  const assignDeviceToClinicPatient = async (params: {
    clinicId: number;
    clinicPatientId: number;
    body: AssignDeviceToPatientBody;
  }): IControllerPromiseResponse<void, ISetupServiceError> => {
    try {
      await setupRepository.assignDeviceToClinicPatientAPI(params);
      return { success: true };
    } catch (error) {
      const e = error as PatientCentricApiError;
      return {
        success: false,
        error: { message: e.message, status: e.status },
      };
    }
  };

  return {
    fetchAdminClinics,
    createPatientAccount,
    createPatientAccountV2,
    replacePatientAccount,
    inviteFamilyMember,
    listFamilyMembers,
    assignDeviceToClinicPatient,
  };
};
